import {formatDuration, ThemedLogo, Tooltip, useTheme} from 'bigdatr-style';
import {useViewer} from 'bigdatr-style';
import React from 'react';
import {Text} from 'bigdatr-style';
import {Box, FlexColumn, Grid} from 'bigdatr-style/layout';
import {Flex} from 'bigdatr-style/layout';
import {Clickable} from 'bigdatr-style';
import {MenuItem} from 'bigdatr-style';
import {Link} from 'bigdatr-style';
import {
    IconClock,
    IconMenu,
    IconPerson,
    IconLike,
    IconPeople,
    IconDataEntry,
    IconContentMove
} from 'bigdatr-style/icon';
import {useRoutes} from '~/app/Router';
import {LinkProps} from 'react-router-dom';
import useLastUsedMediaValuePage from '~/feature/media-value/useLastUsedMediaValuePage';
import {AU} from 'bigdatr-style';
import {useCountry} from '~/feature/country/CountryContext';
import {
    Chart,
    Coin,
    Home1,
    Image1,
    ListPointers,
    PinpaperFilled,
    CalendarCheck,
    Laptop
} from 'react-swm-icon-pack';
import {useGlobalState} from './GlobalState';
type Props = {
    changeTeam: (teamId: string) => void;
    toggleNav: () => void;
    navOpen: boolean;
    showLinks: boolean;
};

const dataentryHost = process.env.BIGDATR_CLIENT_DATAENTRY_HOST;
const iconSize = '1.5rem';

export default function AuthenticatedNavigation(props: Props) {
    const {toggleNav, navOpen} = props;
    const viewer = useViewer();
    const theme = useTheme();
    const {country, isCountryOnTrial, hasMediaValue, hasCreative} = useCountry();
    const {
        dashboard,
        segmentList,
        userAdminUserList,
        userAdminTeamList,
        contentMover,
        exploreCreative,
        reportList,
        styleguideColors,
        mediaValueExpenditureReport,
        mediaValueComparison,
        mediaValueDistribution,
        keywordList,
        complianceResults,
        componentTesting: developerTestRoute
    } = useRoutes();

    const complianceNavVisible = viewer.currentTeam.canAccessComplianceExplore();
    const complianceNavTo = complianceResults.to({});
    const hasOnlyCompliance = viewer.currentTeam.hasOnlyCompliance;

    const {trialEndDate, isTrialNotExpired, hasTeamAdmin, hasDataEntry} = viewer.currentTeam;

    const onSidebarItemClick = () => {
        if (navOpen && window.matchMedia(`(max-width: ${theme.breakpoints.xlMaxWidth})`).matches) {
            return toggleNav();
        }
    };

    const makeLink = ({
        icon,
        to,
        href,
        label,
        exact,
        newTab,
        testid,
        tooltipAlwaysVisible,
        tooltipText
    }: {
        icon: React.ReactNode;
        label: string;
        to?: LinkProps['to'];
        href?: string;
        exact?: boolean;
        newTab?: boolean;
        testid?: string;
        tooltipAlwaysVisible?: boolean;
        tooltipText?: string;
    }) => (
        <SidebarLink
            to={to}
            href={href}
            showLabel={navOpen}
            icon={icon}
            exact={exact}
            onClick={onSidebarItemClick}
            newTab={newTab}
            testId={testid}
            tooltipAlwaysVisible={tooltipAlwaysVisible}
            tooltipText={tooltipText}
        >
            {label}
        </SidebarLink>
    );

    const [lastUsedMediaValuePage] = useLastUsedMediaValuePage();
    const {lastState} = useGlobalState().mediaExpenditureState;

    const makeMediaValueLink = () => {
        const commonProps = {
            label: 'Media Value',
            icon: <Coin />,
            testid: 'appcues-app-navigation-media-value-link'
        };

        let to: LinkProps['to'] = {};
        switch (lastUsedMediaValuePage) {
            default:
            case 'Data':
                to = mediaValueExpenditureReport.to(lastState ?? {});
                break;

            case 'Comparison':
                to = mediaValueComparison.to({});
                break;

            case 'Distribution':
                to = mediaValueDistribution.to({});
                break;
        }
        return makeLink({...commonProps, to});
    };

    const staffAdminLinks = (hasTeamAdmin || hasDataEntry) && (
        <Section title="Bigdatr Admin" collapsed={!navOpen}>
            {hasDataEntry &&
                makeLink({
                    label: 'Component testing',
                    icon: <Laptop size={iconSize} />,
                    to: developerTestRoute.to()
                })}
            {makeLink({
                label: 'Styleguide',
                icon: <IconLike size={iconSize} />,
                to: styleguideColors.to()
            })}
            {hasTeamAdmin && (
                <>
                    {makeLink({
                        label: 'Account Management',
                        icon: <IconPeople size={iconSize} />,
                        to: userAdminTeamList.to({})
                    })}
                    {makeLink({
                        label: 'User Management',
                        icon: <IconPerson size={iconSize} />,
                        to: userAdminUserList.to({})
                    })}
                    {makeLink({
                        label: 'Content Mover',
                        icon: <IconContentMove size={iconSize} />,
                        to: contentMover.to()
                    })}
                </>
            )}
            {hasDataEntry && (
                <>
                    {makeLink({
                        label: 'Data Entry',
                        icon: <IconDataEntry size={iconSize} />,
                        href: dataentryHost
                    })}
                </>
            )}
        </Section>
    );

    const sidebarLinks = () => {
        return (
            <FlexColumn gap={4}>
                <Section title="Main Menu" collapsed={!navOpen}>
                    {country === AU && !hasOnlyCompliance && (
                        <SidebarLink exact icon={<Home1 />} to={dashboard.to()} showLabel={navOpen}>
                            Dashboard
                        </SidebarLink>
                    )}
                    {hasCreative && (
                        <SidebarLink
                            testId="appcues-app-navigation-creatives-link"
                            icon={<Image1 />}
                            to={exploreCreative.to({})}
                            showLabel={navOpen}
                        >
                            Advertising Creative
                        </SidebarLink>
                    )}

                    {hasMediaValue && makeMediaValueLink()}

                    {complianceNavVisible && country === AU && (
                        <SidebarLink
                            icon={<CalendarCheck />}
                            to={complianceNavTo}
                            showLabel={navOpen}
                        >
                            Compliance
                        </SidebarLink>
                    )}

                    {(hasMediaValue || hasCreative) && (
                        <SidebarLink
                            testId="appcues-app-navigation-reports-link"
                            icon={<PinpaperFilled />}
                            to={reportList.to()}
                            showLabel={navOpen}
                        >
                            Reports
                        </SidebarLink>
                    )}

                    {country === AU && (hasMediaValue || hasCreative) && (
                        <SidebarLink icon={<Chart />} to={segmentList.to()} showLabel={navOpen}>
                            Segments
                        </SidebarLink>
                    )}

                    {country !== AU && (
                        <SidebarLink
                            icon={<ListPointers style={{shapeRendering: 'crispEdges'}} />}
                            to={keywordList.to({})}
                            showLabel={navOpen}
                        >
                            Keywords
                        </SidebarLink>
                    )}
                </Section>

                {staffAdminLinks}
            </FlexColumn>
        );
    };

    return (
        <FlexColumn
            height="100%"
            overflowY={{_: navOpen ? 'auto' : 'hidden', md: 'auto'}}
            borderRight={{md: 'outline'}}
            backgroundColor="navigationBackground"
        >
            {/* Fixed height is to prevent the hamburger icon from jumping with a closed nav */}
            <Flex gap={3} p={3} justifyContent="space-between" height="4rem">
                <Link to={dashboard.to()} display={{md: navOpen ? 'block' : 'none'}}>
                    <ThemedLogo />
                </Link>
                <Clickable onClick={toggleNav} aria-label="Toggle menu">
                    <IconMenu size={iconSize} />
                </Clickable>
            </Flex>

            {isCountryOnTrial && trialEndDate && isTrialNotExpired && (
                <Box pb={3}>
                    {makeLink({
                        label: `Trial ends in ${formatDuration(
                            new Date(),
                            new Date(trialEndDate)
                        )}`,
                        icon: <IconClock size={iconSize} />,
                        to: '/settings/billing'
                    })}
                </Box>
            )}
            {sidebarLinks()}
        </FlexColumn>
    );
}

function SidebarLink({
    children,
    icon,
    to,
    href,
    showLabel,
    noTooltip,
    testId,
    exact,
    onClick,
    newTab,
    tooltipAlwaysVisible,
    tooltipText
}: {
    children: string;
    icon: React.ReactNode;
    showLabel: boolean;
    to?: LinkProps['to'];
    href?: string;
    noTooltip?: boolean;
    active?: boolean;
    testId?: string;
    exact?: boolean;
    onClick?: () => void;
    newTab?: boolean;
    tooltipAlwaysVisible?: boolean;
    tooltipText?: string;
}) {
    let tooltipContent = showLabel || noTooltip ? undefined : children;
    if (tooltipAlwaysVisible) tooltipContent = tooltipText;
    return (
        <Tooltip content={tooltipContent} placement="right" alwaysVisible={tooltipAlwaysVisible}>
            <MenuItem
                small
                data-testid={testId}
                exact={exact}
                to={to}
                href={href}
                navLink
                onClick={onClick}
                target={newTab ? '_blank' : undefined}
                aria-label={children}
            >
                <Grid gridTemplateColumns={`${iconSize} auto`} gap={2} alignItems="center">
                    {icon}
                    {showLabel && children}
                </Grid>
            </MenuItem>
        </Tooltip>
    );
}

function Section({
    title,
    children,
    collapsed
}: {
    title: string;
    children: React.ReactNode;
    collapsed: boolean;
}) {
    return (
        <FlexColumn gap={1}>
            {!collapsed && <Text px={3} textStyle="smallLabel" color="muted" children={title} />}
            {children}
        </FlexColumn>
    );
}
